import * as React from 'react'
import {
  Box, Typography, TextField, FormControl, Select, MenuItem, Button, Alert, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, Pagination,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'

import {sectionCardSx, filterBarSx, dataGridWrapSx, headerCellSx, cellSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, SUCCESS, WARNING, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'

import {kg, int} from '../format'
import {ARROW, withinTolerance, governedBy, packById, chargeable, round2} from './state/packingModel'
import NumberCell from './components/NumberCell'
import EmptyState from '../components/EmptyState'

const PER_PAGE = 10

const panelAlertSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 0.75, '& .MuiAlert-message': {lineHeight: 1.6}}
const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const fieldSx = {fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 170}
// Menu is portalled to document.body, outside the shell's zoom — matches
// disputes-v2/FilterBar.js's own selectMenuProps so an open menu lines up
// with its now-SIZE['2xs'] closed control.
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}}

/**
 * Weight Freeze (v2) — Packing Task 7: the base-data grid.
 *
 * This is the CRUD surface for the dead/volumetric/chargeable split that
 * Initiate pre-fills from and Analytics reports on. Every pure derivation a
 * cell needs is a plain exported function below, never inlined in JSX, so
 * each is testable without mounting the table — same split as
 * SubcategoryPanel.js's own rowSignature/matchesSearch/nextSort.
 *
 * INTERFACE NOTE — deviated from the task brief on purpose, and said so
 * rather than quietly matching it: the brief's own "Interfaces" line names
 * `actions.patchSku`/`actions.setSkuPack`. Neither exists anywhere in this
 * module — `state/useFreezeStore.js` exposes `actions.updateSku(sku, patch)`
 * for a generic field patch and `actions.upsertPack(patch)` for a
 * material-level edit. There is no third action for "point this ONE SKU at
 * a different EXISTING material" (that's a new operation this grid
 * introduces, not one UPSERT_PACK/REMOVE_PACK already cover), so this file
 * computes the resulting `volKg`/`chargeKg` patch itself — via
 * `packChangePatch` below, mirroring `REMOVE_PACK`'s own
 * `dest.volKg != null` gate — and writes it through `actions.updateSku`.
 * The brief's own Step 2 snippet has the same gap: it patches `volKg` alone
 * on the drift-revert click, which would leave `chargeKg` stale and
 * disagreeing with the very figure just accepted — exactly the
 * "grid disagrees with the drawer beside it" bug the brief itself warns
 * against. `volWeightPatch` below recomputes both together.
 */

/** `s.mpw > 0 && !withinTolerance(s.chargeKg || 0, s.mpw)` — verbatim from the brief. mpw<=0 (unshipped) never counts as "disagrees". */
export function offBase(s) {
  return s.mpw > 0 && !withinTolerance(s.chargeKg || 0, s.mpw)
}

/** How many SKUs currently disagree with their own observed most-probable weight. Drives the toggle's "(N)". */
export function countOff(skus) {
  return (skus || []).filter(offBase).length
}

/**
 * The pagination trap's signature: SKU code plus cat/sub pairing, joined
 * across every row. Deliberately excludes deadKg/volKg/chargeKg — editing a
 * weight must not change row identity, or the table repaginates under the
 * merchant's cursor. Hoisted to module scope (rather than the brief's own
 * inline `React.useMemo` body) because it closes over nothing: a plain
 * function is trivially unit-testable and never needs a dependency array
 * of its own.
 */
export function identitySig(skus) {
  return (skus || []).map((s) => `${s.sku}|${s.cat}>${s.sub}`).join('~')
}

/** The ONE case where the row set legitimately depends on the weights: the disagreement toggle. Empty string when the toggle is off, so it never forces a recompute it isn't responsible for. */
export function offSigOf(skus, offOnly) {
  return offOnly ? (skus || []).map((s) => (offBase(s) ? '1' : '0')).join('') : ''
}

/**
 * The filtered SKU codes for the current search / sub-category / disagree
 * toggle. Returns codes, not row objects — `BaseDataPanel` looks each one up
 * through a `byCode` map built fresh from `state.skus` every render, so a
 * live edit never has to flow back through this function to reach the cell
 * showing it.
 */
export function computeRowCodes(skus, {q, subFilter, offOnly}) {
  const needle = (q || '').trim().toLowerCase()
  return (skus || [])
    .filter((s) => {
      if (subFilter !== 'all' && `${s.cat}${ARROW}${s.sub}` !== subFilter) return false
      if (offOnly && !offBase(s)) return false
      if (needle && `${s.sku} ${s.cat} ${s.sub}`.toLowerCase().indexOf(needle) < 0) return false
      return true
    })
    .map((s) => s.sku)
}

/**
 * The Volumetric cell's drift affordance: does the packing material's own
 * `volKg` (`implied`) disagree with this SKU's stored `volKg`, beyond the
 * shared tolerance? Both gates below are `!= null`, never a falsy check —
 * `implied` can legitimately be `0` for a carton under 25 cm³, and a flyer
 * (no dims, `pack.volKg === null`) must never register as "implies 0".
 * This exact `== null` vs falsy distinction is the bug the brief flags as
 * having shipped once already and been caught in review.
 */
export function volDrift(sku, packs) {
  const pack = packById(packs, sku.packId)
  const implied = pack && pack.volKg != null ? pack.volKg : null
  const drifted = implied != null && !withinTolerance(implied, sku.volKg || 0)
  return {implied, drifted}
}

/** 'dead governs' | 'volumetric governs' | 'dead and volumetric level' for the Chargeable cell's sub-line. */
export function chargeGovernLine(deadKg, volKg) {
  const governs = governedBy(deadKg, volKg)
  return governs === 'level' ? 'dead and volumetric level' : `${governs} governs`
}

/**
 * The Observed cell's agreement line. Caller must gate on `sku.mpw` truthy
 * first — this never renders the "Not shipped yet" state itself, so it can
 * stay a plain comparison rather than a third branch repeating that gate.
 */
export function observedAgreement(sku) {
  const agrees = withinTolerance(sku.chargeKg || 0, sku.mpw)
  if (agrees) return {tone: 'ok', text: 'computed agrees'}
  const gap = round2((sku.chargeKg || 0) - sku.mpw)
  return {tone: 'warn', text: `computed is ${kg(Math.abs(gap))} ${gap > 0 ? 'heavier' : 'lighter'}`}
}

/** Dead-weight commit: recomputes chargeKg via the shared max(dead, vol), never a bare comparison. */
export function deadWeightPatch(sku, nextDeadKg) {
  return {deadKg: nextDeadKg, chargeKg: chargeable(nextDeadKg, sku.volKg)}
}

/** Volumetric commit (typed OR the drift-revert button): same cascade as deadWeightPatch, the other side of max(dead, vol). */
export function volWeightPatch(sku, nextVolKg) {
  return {volKg: nextVolKg, chargeKg: chargeable(sku.deadKg, nextVolKg)}
}

/**
 * Re-pointing a SKU at a different, EXISTING packing material (the material
 * Select cell). `null` pack means "Not set". Mirrors `REMOVE_PACK`'s own
 * convention in useFreezeStore.js: a destination with real dimensions takes
 * over the volumetric figure; a boxless one (or "Not set") leaves the SKU's
 * existing volKg alone rather than zeroing it — a flyer implies no
 * volumetric figure, not a zero one. `!= null`, never falsy, for the same
 * reason as volDrift above.
 */
export function packChangePatch(sku, pack) {
  const volKg = pack && pack.volKg != null ? pack.volKg : sku.volKg
  return {packId: pack ? pack.id : null, volKg, chargeKg: chargeable(sku.deadKg, volKg)}
}

/**
 * The base-data grid — Packing Task 7. `state`/`actions` are the narrow
 * slice and action set `PackingPage` threads down from the one module-wide
 * store; this panel holds no SKU data itself, only its own filters/page —
 * every mutation goes through `actions.updateSku`, which already lands in
 * the one `skus` array every other section reads.
 */
export default function BaseDataPanel({state, actions}) {
  const {skus, packs} = state

  const [q, setQ] = React.useState('')
  const [subFilter, setSubFilter] = React.useState('all')
  const [offOnly, setOffOnly] = React.useState(false)
  const [page, setPage] = React.useState(1)

  // Live lookup for whatever IS on the current page — recomputed on every
  // `skus` change (weight edits included), because a cell must always show
  // the record's current value. This is deliberately NOT the same memo as
  // `rowCodes` below, which must NOT recompute on a weight edit.
  const byCode = React.useMemo(() => new Map(skus.map((s) => [s.sku, s])), [skus])

  const idSig = React.useMemo(() => identitySig(skus), [skus])
  const offSig = offSigOf(skus, offOnly)

  // The pagination trap, in code: deps are the SIGNATURE STRING (identical
  // across a weight edit) plus the filters and `offSig` — never `skus`
  // itself. See identitySig's/offSigOf's own doc comments for why.
  const rowCodes = React.useMemo(
    () => computeRowCodes(skus, {q, subFilter, offOnly}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idSig, q, subFilter, offOnly, offSig],
  )

  // Distinct cat/sub pairs for the filter Select — same signature dependency
  // as rowCodes, so a weight edit doesn't re-walk all 111 SKUs for nothing.
  const subOptions = React.useMemo(() => {
    const set = new Set(skus.map((s) => `${s.cat}${ARROW}${s.sub}`))
    return Array.from(set).sort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idSig])

  const offCount = countOff(skus)
  const hasActiveFilter = !!q || subFilter !== 'all' || offOnly

  // Page resets on a FILTER change only, never on `rowCodes` itself.
  React.useEffect(() => { setPage(1) }, [q, subFilter, offOnly])

  const pages = Math.max(1, Math.ceil(rowCodes.length / PER_PAGE))
  const clampedPage = Math.min(page, pages)
  const pageCodes = rowCodes.slice((clampedPage - 1) * PER_PAGE, clampedPage * PER_PAGE)
  const pageRows = pageCodes.map((code) => byCode.get(code)).filter(Boolean)

  const resetFilters = () => { setQ(''); setSubFilter('all'); setOffOnly(false) }

  const handlePackChange = (sku, nextPackId) => {
    const pack = nextPackId ? packById(packs, nextPackId) : null
    actions.updateSku(sku.sku, packChangePatch(sku, pack))
  }

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{mb: 1.5}}>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
          Base data
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, lineHeight: 1.6, maxWidth: 760}}>
          <Box component='span' sx={{fontWeight: WEIGHT.bold, color: NAVY}}>
            &quot;The SKU anchors dead weight, the packing material anchors volumetric weight.&quot;
          </Box>
          {' '}Chargeable weight is the higher of the two, so a light product in a big carton bills on the
          carton. Every figure here is editable and saves when you leave the field or press Enter. Escape
          puts a cell back the way it was.
        </Typography>
      </Box>

      <Alert severity='info' sx={{...panelAlertSx, mb: 1.5}}>
        Prozo can also edit this base data from the admin panel. Whatever is set there is what shows here,
        and a freeze request is priced off these figures.
      </Alert>

      <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5}}>
        <TextField
          size='small'
          placeholder='Search SKU, category, sub-category'
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
              </InputAdornment>
            ),
          }}
          inputProps={{'aria-label': 'Search base data'}}
          sx={{...fieldSx, minWidth: 260}}
        />

        <FormControl size='small' sx={fieldSx}>
          <Select
            value={subFilter}
            onChange={(e) => setSubFilter(e.target.value)}
            inputProps={{'aria-label': 'Filter by sub-category'}}
            MenuProps={selectMenuProps}
          >
            <MenuItem value='all'>All sub-categories</MenuItem>
            {subOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <Button
          size='small'
          aria-pressed={offOnly}
          onClick={() => setOffOnly((v) => !v)}
          sx={{
            ...secondaryBtnSx,
            ...(offOnly ? {bgcolor: withOpacity(WARNING, 0.16), borderColor: WARNING, color: NAVY} : {}),
          }}
        >
          Disagrees with observed ({int(offCount)})
        </Button>

        {hasActiveFilter && (
          <Button
            size='small'
            onClick={resetFilters}
            sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}
          >
            Clear
          </Button>
        )}

        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto', whiteSpace: 'nowrap'}}>
          {int(rowCodes.length)} of {int(skus.length)} SKUs
        </Typography>
      </Box>

      {rowCodes.length === 0 ? (
        <EmptyState
          icon={SearchOffIcon}
          title='No SKUs match'
          body='Try a different search, or clear the sub-category and disagreement filters.'
          action={<Button sx={secondaryBtnSx} onClick={resetFilters}>Clear filters</Button>}
        />
      ) : (
        <Box sx={dataGridWrapSx}>
          <Table sx={{minWidth: 1040}}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>SKU code</TableCell>
                <TableCell sx={headerCellSx}>Sub-category</TableCell>
                <TableCell sx={headerCellSx}>Packing material</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Dead weight</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Volumetric</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Chargeable</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Observed most probable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((sku) => {
                const {implied, drifted} = volDrift(sku, packs)
                const governLine = chargeGovernLine(sku.deadKg, sku.volKg)
                const agreement = sku.mpw ? observedAgreement(sku) : null

                return (
                  // This row carries no onClick — every edit here happens
                  // inside a cell (the Select, the two NumberCells, the
                  // revert button), never on the row itself. If a LATER
                  // change makes the row clickable, each of those needs its
                  // own onClick={(e) => e.stopPropagation()}, same as
                  // NumberCell already does internally — otherwise clicking
                  // into a cell to edit it would also fire the row handler.
                  // Not added now: a stopPropagation with nothing to stop
                  // is dead code.
                  <TableRow key={sku.sku} hover>
                    <TableCell sx={cellSx}>
                      <Typography
                        title={sku.sku}
                        sx={{
                          fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY,
                          maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {sku.sku}
                      </Typography>
                      <Typography sx={subtleTextSx}>{int(sku.ships)} shipments</Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>
                        {sku.sub}
                      </Typography>
                      <Typography sx={subtleTextSx}>{sku.cat}</Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <FormControl size='small' sx={{minWidth: 160}}>
                        <Select
                          value={sku.packId || ''}
                          onChange={(e) => handlePackChange(sku, e.target.value || null)}
                          inputProps={{'aria-label': `Packing material for ${sku.sku}`}}
                          sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value=''>Not set</MenuItem>
                          {packs.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <NumberCell
                        value={sku.deadKg}
                        suffix='kg'
                        label={`Dead weight in kg for ${sku.sku}`}
                        onCommit={(next) => actions.updateSku(sku.sku, deadWeightPatch(sku, next))}
                      />
                    </TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <NumberCell
                        value={sku.volKg}
                        suffix='kg'
                        label={`Volumetric weight in kg for ${sku.sku}`}
                        onCommit={(next) => actions.updateSku(sku.sku, volWeightPatch(sku, next))}
                      />
                      {drifted && (
                        <Button
                          size='small'
                          onClick={() => actions.updateSku(sku.sku, volWeightPatch(sku, implied))}
                          sx={{
                            display: 'block', ml: 'auto', mt: 0.25, minWidth: 0, p: 0,
                            fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE,
                          }}
                        >
                          Material implies {kg(implied)}
                        </Button>
                      )}
                    </TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
                        {kg(sku.chargeKg)}
                      </Typography>
                      <Typography sx={subtleTextSx}>{governLine}</Typography>
                    </TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      {!sku.mpw ? (
                        <Typography sx={subtleTextSx}>Not shipped yet</Typography>
                      ) : (
                        <>
                          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
                            {kg(sku.mpw)}
                          </Typography>
                          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: agreement.tone === 'ok' ? SUCCESS : WARNING}}>
                            {agreement.text}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {pages > 1 && (
            <Box sx={{display: 'flex', justifyContent: 'flex-end', p: 1.5}}>
              <Pagination
                size='small'
                page={clampedPage}
                count={pages}
                onChange={(e, p) => setPage(p)}
                sx={{'& .MuiPaginationItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
